"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { healthApi } from "@/lib/api";

interface ConnectionContextType {
  isBackendAvailable: boolean;
  isChecking: boolean;
  lastChecked: Date | null;
  checkConnection: () => Promise<void>;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export function ConnectionProvider({ children }: { children: React.ReactNode }) {
  const [isBackendAvailable, setIsBackendAvailable] = useState<boolean>(true);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkConnection = useCallback(async () => {
    setIsChecking(true);
    try {
      const isAvailable = await healthApi.check();
      setIsBackendAvailable(isAvailable);
      setLastChecked(new Date());
    } catch (error) {
      console.error('Health check failed:', error);
      setIsBackendAvailable(false);
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Check connection on mount and periodically
  useEffect(() => {
    // Initial check
    checkConnection();

    // Set up periodic checks every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    return () => clearInterval(interval);
  }, [checkConnection]);

  return (
    <ConnectionContext.Provider
      value={{
        isBackendAvailable,
        isChecking,
        lastChecked,
        checkConnection,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error('useConnection must be used within ConnectionProvider');
  }
  return context;
}
